import { ViewModel, View, ObservableArray, DelegatedObservableArrayChangeListenner, ObservableArrayChangeListenner } from "binder"
import { ControlPlanView } from "./controlPlan-view"
import { WindowViewModel } from "src/common/userMainWindow/windowContainer/windowContainer-viewModel";
import { ProductSelector, ProductType } from "src/erp/inventory/rawData/product/productSelector";
import { MeasureParameterGroupSelector } from "../measuredParameterGroup/selectors/measureParameterGroupSelector";
import { ServerConnection } from "src/server/serverConnection";
import { PaginationInfo, ServerSideDataLoader } from "uicomponents";
import { ControlPlanVersionEditorViewModel, ControlPlanVersionInserter, ControlPlanVersionUpdater } from "./ControlPlanVersionEditor/controlPlanVersionEditor-viewModel";
import { SelectorBaseView } from "src/common/reusableComponents/selectorBase/selectorBase-view";
import { SelectorBaseViewModel } from "src/common/reusableComponents/selectorBase/selectorBase-viewModel";
import { PersonnelSearchViewModel } from "src/erp/enterpriseAdministration/humanResource/personnel/personnelSearch/personnelSearch-viewModel";

export class ControlPlanViewModel extends WindowViewModel {
    public getTitle(): string {
        return "طرح کنترل";
    }
    GetDefaultView(): View<ViewModel> {
        return new ControlPlanView();
    }
    public getTypeId(): string {
        return "VM-AE42F540-1A49-4BF8-BC98-38471F3D4779"
    }

    constructor(parentViewModel: ViewModel) {
        super(parentViewModel);
        this.productSearchableSelector = new ProductSelector(this, ProductType.FinalGood);
        this.productSearchableSelector.onSelectionChanged = () => this.selectorsOnSelectionChanged();

        this.measureParameterGroupSelector = new MeasureParameterGroupSelector(this, false);
        this.measureParameterGroupSelector.onSelectionChanged = () => this.selectorsOnSelectionChanged();

        this.controlPlanVersions = ObservableArray.create<ControlPlanVersionViewModel>();
        this.loader = new ServerSideDataLoader(filteringInformation => this.loadControlPlanVersions(filteringInformation), 30);
        this.canCreateNewControlPlanVersion = false;
        this.isActiveVersiondChanged = false;
    }

    public set isLoading(v: boolean) { this._isLoading = v; } public get isLoading(): boolean { return this._isLoading; } private _isLoading: boolean;
    public set loader(v: ServerSideDataLoader) { this._loader = v; } public get loader(): ServerSideDataLoader { return this._loader; } private _loader: ServerSideDataLoader;
    public set productSearchableSelector(v: ProductSelector) { this._productSearchableSelector = v; } public get productSearchableSelector(): ProductSelector { return this._productSearchableSelector; } private _productSearchableSelector: ProductSelector;
    public set productId(v: string) { this._productId = v; } public get productId(): string { return this._productId; } private _productId: string;
    public set measureParameterGroupSelector(v: MeasureParameterGroupSelector) { this._measureParameterGroupSelector = v; } public get measureParameterGroupSelector(): MeasureParameterGroupSelector { return this._measureParameterGroupSelector; } private _measureParameterGroupSelector: MeasureParameterGroupSelector;
    public set measureParameterGroupId(v: string) { this._measureParameterGroupId = v; } public get measureParameterGroupId(): string { return this._measureParameterGroupId; } private _measureParameterGroupId: string;
    public set controlPlanId(v: string) { this._controlPlanId = v; } public get controlPlanId(): string { return this._controlPlanId; } private _controlPlanId: string;
    public set controlPlanVersions(v: ObservableArray<ControlPlanVersionViewModel>) { this._controlPlanVersions = v; } public get controlPlanVersions(): ObservableArray<ControlPlanVersionViewModel> { return this._controlPlanVersions; } private _controlPlanVersions: ObservableArray<ControlPlanVersionViewModel>;
    public set canCreateNewControlPlanVersion(v: boolean) { this._canCreateNewControlPlanVersion = v; } public get canCreateNewControlPlanVersion(): boolean { return this._canCreateNewControlPlanVersion; } private _canCreateNewControlPlanVersion: boolean;
    public set isActiveVersiondChanged(v: boolean) { this._isActiveVersiondChanged = v; } public get isActiveVersiondChanged(): boolean { return this._isActiveVersiondChanged; } private _isActiveVersiondChanged: boolean;

    async selectorsOnSelectionChanged(): Promise<void> {
        if (this.productSearchableSelector.selectedItem != null && this.measureParameterGroupSelector.selectedItem != null) {
            this.productId = this.productSearchableSelector.selectedItem.id;
            this.measureParameterGroupId = this.measureParameterGroupSelector.selectedItem.id;
            this.canCreateNewControlPlanVersion = true;
            await this.loader.refresh();
            return;
        }

        this.canCreateNewControlPlanVersion = false;
        this.controlPlanVersions.clear();
    }

    async loadControlPlanVersions(filteringInformation: any): Promise<PaginationInfo> {
        try {
            this.isLoading = true;
            let serverResult = await ServerConnection.callServer('/Complex/ControlPlanService/GetControlPlanVersions', {
                FilteringInformation: filteringInformation,
                ProductId: this.productId,
                MeasureParameterGroupId: this.measureParameterGroupId
            });

            this.controlPlanVersions.update(...serverResult.Versions.map(item => {
                let controlPlanVersion: ControlPlanVersionViewModel = new ControlPlanVersionViewModel(this);
                controlPlanVersion.id = item.Id;
                controlPlanVersion.no = item.No;
                controlPlanVersion.isActive = item.IsActive;
                return controlPlanVersion;
            }));
            this.controlPlanId = serverResult.ControlPlanId;
            this.isActiveVersiondChanged = false;

            return new PaginationInfo(serverResult.PaginationInfo?.CurrentPage ?? 1, serverResult.PaginationInfo?.PageCount ?? 1);
        } catch (ex) {
            console.log(ex);
            return new PaginationInfo(0, 0);
        } finally {
            this.isLoading = false;
        }
    }

    async createControlPlanVersion(): Promise<void> {
        let popup = new ControlPlanVersionEditorViewModel(this, new ControlPlanVersionInserter(), this.measureParameterGroupId, this.productId);
        let result = await this.showPopup(popup);
        if (result != null) {
            this.loader.refresh();
        }
    }

    async editControlPlanVersion(versionId: string) {
        let popup = new ControlPlanVersionEditorViewModel(this, new ControlPlanVersionUpdater(), this.measureParameterGroupId, this.productId);
        await popup.editLoadById(versionId);
        let result = await this.showPopup(popup);
        if (result != null) {
            this.loader.refresh();
        }
    }

    async duplicateControlPlanVersion(versionId: string) {
        let popup = new ControlPlanVersionEditorViewModel(this, new ControlPlanVersionInserter(), this.measureParameterGroupId, this.productId);
        await popup.duplicateLoadById(versionId);
        let result = await this.showPopup(popup);
        if (result != null) {
            this.loader.refresh();
        }
    }

    controlPlanActiveVersionChanged(activeVersion: ControlPlanVersionViewModel): void {
        if (activeVersion.isActive) {
            this.isActiveVersiondChanged = true;
            this.controlPlanVersions.forEach(item => {
                if (item != activeVersion) {
                    item.isActive = false;
                }
            })
        }
    }

    async changeControlPlanActiveVersion(): Promise<void> {
        try {
            this.isLoading = true;
            let activeVersion = this.controlPlanVersions.find(item => item.isActive);
            let serverResult = await ServerConnection.callServer('/Complex/ControlPlanService/SetActiveControlPlanVersion', {
                ControlPlanId: this.controlPlanId,
                VersionId: activeVersion?.id
            });

            if (serverResult.IsSuccessful) {
                await this.messageBoxConfirmSuccess("نسخه فعال طرح کنترل با موفقیت تغییر کرد.", "نسخه فعال");
            }
        } catch (ex) {
            console.log(ex);
        } finally {
            this.isLoading = false;
        }
    }
}

export class ControlPlanVersionViewModel extends ViewModel {
    public getTypeId(): string { return ''; }
    public GetDefaultView(): View<ViewModel> { return null; }
    constructor(parent: ControlPlanViewModel) {
        super(parent);
        this._parent = parent;
    }

    private _parent: ControlPlanViewModel;
    public set id(v: string) { this._id = v; } public get id(): string { return this._id; } private _id: string;
    public set no(v: number) { this._no = v; } public get no(): number { return this._no; } private _no: number;
    public set isActive(v: boolean) { this._isActive = v; this.changeActivationStatus() } public get isActive(): boolean { return this._isActive; } private _isActive: boolean;

    edit(): void {
        this._parent.editControlPlanVersion(this.id);
    }

    duplicate(): void {
        this._parent.duplicateControlPlanVersion(this.id);
    }

    changeActivationStatus(): void {
        this._parent.controlPlanActiveVersionChanged(this);
    }
}

export class ControlPlanMeasureParameterSpecification extends ViewModel {
    public getTypeId(): string { return ''; }
    public GetDefaultView(): View<ViewModel> { return null; }
    constructor(parent: ViewModel) {
        super(parent);

        this.valueSelector = new SpecificationValueSelector(this);
        this.valueSelector.onSelectionChanged = selectedValue => {
            if (selectedValue != null && this.selectedValues.some(value => value.valueId == selectedValue.valueId) == false) {
                this.selectedValues.push(selectedValue);
                this.valueSelector.selectedItem = null;
            }
        }

        this.values = ObservableArray.create<SpecificationValue>();
        this.selectedValues = ObservableArray.create<SpecificationValue>();
    }

    public set id(v: string) { this._id = v; } public get id(): string { return this._id; } private _id: string;
    public set title(v: string) { this._title = v; } public get title(): string { return this._title; } private _title: string;
    public set valueSelector(v: SpecificationValueSelector) { this._valueSelector = v; } public get valueSelector(): SpecificationValueSelector { return this._valueSelector; } private _valueSelector: SpecificationValueSelector;
    public set values(v: ObservableArray<SpecificationValue>) { this._values = v; } public get values(): ObservableArray<SpecificationValue> { return this._values; } private _values: ObservableArray<SpecificationValue>;
    public set selectedValues(v: ObservableArray<SpecificationValue>) { this._selectedValues = v; } public get selectedValues(): ObservableArray<SpecificationValue> { return this._selectedValues; } private _selectedValues: ObservableArray<SpecificationValue>;

    updateValueSelector() {
        this.valueSelector.values.update(...this.values);
        this.valueSelector.refresh();
    }

    removeValue(value:SpecificationValue){
        this.selectedValues.remove(value);
    }
}

class SpecificationValueSelector extends SelectorBaseViewModel<SpecificationValue> {
    constructor(parent: ViewModel) {
        super(parent, true);
        this.values = ObservableArray.create<SpecificationValue>();
    }

    public set values(v: ObservableArray<SpecificationValue>) { this._values = v; } public get values(): ObservableArray<SpecificationValue> { return this._values; } private _values: ObservableArray<SpecificationValue>;
    protected loadData(): Promise<SpecificationValue[]> {
        return Promise.resolve(this.values);
    }
}

export class SpecificationValue extends ViewModel {
    public getTypeId(): string { return ''; }
    public GetDefaultView(): View<ViewModel> { return null; }
    constructor(parent: ControlPlanMeasureParameterSpecification) {
        super(parent);
        this._parent = parent;
    }

    private _parent: ControlPlanMeasureParameterSpecification;
    public set id(v: string) { this._id = v; } public get id(): string { return this._id; } private _id: string;
    public set title(v: string) { this._title = v; } public get title(): string { return this._title; } private _title: string;
    public set valueId(v: string) { this._valueId = v; } public get valueId(): string { return this._valueId; } private _valueId: string;
    public set value(v: string) { this._value = v; this._title = v } public get value(): string { return this._value; } private _value: string;

    remove() {
        this._parent.removeValue(this);
    }
}